/**
 * ProShip Direct v2 — route table.
 *
 * Kept in its own file so routes.config.js takes a two-line diff, matching how Ashvini's module
 * mounts. Everything is additive: no existing route is modified, and with the flag off this
 * exports an empty array so the panel is byte-identical to today.
 *
 * Two placement decisions from the brief:
 *   - The sidebar group sits AFTER PTL Freight (see SIDEBAR_GROUPS in menu/Sidebar.js).
 *   - ProShip Direct Performance is in the GENERAL group, which renders as
 *     "PERFORMANCE & ANALYTICS" — it is analytics, read to decide, not setup.
 *
 * Pages reached from the Settings page (Brand Setup, and courier rules and PACE) are registered
 * here but not shown in the sidebar: Settings is their entry point, and duplicating them in the
 * nav would imply they belong to Direct when they are common panel features.
 *
 * Roles mirror ROLE_COMBINATIONS from routes.config.js, declared locally because routes.config
 * imports this file and importing it back would be a circular dependency. Keep them in step.
 */
import React from 'react';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';

import { isDirectPlanEnabled, dpPath, SETTINGS_PATHS } from './flags';
import { SETUP_DIMENSIONS } from './data/defaults';

import Home from './pages/Home';
import MyCouriers from './pages/MyCouriers';
import ConnectCourier from './pages/ConnectCourier';
import CourierSetup from './pages/CourierSetup';
import CourierAvailability from './pages/CourierAvailability';
import ReturnsQc from './pages/ReturnsQc';
import ShippingRules from './pages/ShippingRules';
import RuleBuilder from './pages/RuleBuilder';
import PaceEngine from './pages/PaceEngine';
import CourierPriority from './pages/CourierPriority';
import ProductMaster from './pages/ProductMaster';
import DirectPerformance from './pages/DirectPerformance';
import LabelConfig from './pages/LabelConfig';

// Mirrors ROLE_COMBINATIONS.B2C_ORDERS and .SETTINGS_ACCESS.
const ORDER_ROLES = ['ORDERS', 'B2C_ACCESS'];
const SETTINGS_ROLES = ['SETTINGS'];

/**
 * `enableForConfig` is THE master switch, per merchant: MerchantConfig.isProzoDirectEnabled,
 * surfaced through config/merchant into orderConversionDetails. isConfigRouteVisible treats an
 * unknown key as OPT-IN, so absent or false hides every route below — which is every existing
 * merchant, with no backfill. The backend refuses the same merchants at /api/direct/**, so the
 * menu and the API cannot disagree.
 *
 * This replaces REACT_APP_DIRECT_PLAN as the gate that matters. That env flag still controls
 * whether the module is BUILT at all; this controls who may see it.
 */
const base = {
  group: 'DIRECT_PLAN',
  zone: 'DIRECT_PLAN',
  vertical: 'BOTH',
  roles: ORDER_ROLES,
  enableForConfig: 'isProzoDirectEnabled',
};

const routes = [
  {
    ...base,
    pathname: dpPath('/home'),
    icon: <RocketLaunchOutlinedIcon />,
    label: 'Home',
    id: 'menu-dp-home',
    displayInSidebar: true,
    component: <Home />,
  },
  {
    ...base,
    pathname: dpPath('/couriers'),
    icon: <LocalShippingOutlinedIcon />,
    label: 'My Couriers',
    id: 'menu-dp-couriers',
    displayInSidebar: true,
    // No submenu: "All accounts" and "Connect a courier" were a two-item dropdown in front of a
    // page that already carries a Connect button. Clicking the nav item lands on the list.
    component: <MyCouriers />,
  },
  {
    ...base,
    pathname: dpPath('/couriers/connect'),
    label: 'Connect a courier',
    displayInSidebar: false,
    component: <ConnectCourier />,
  },
  {
    // One page, five tabs. It used to be a submenu of five routes, which meant a merchant setting
    // up one account navigated five times and had the account reset under them at every step.
    ...base,
    pathname: dpPath('/courier-setup'),
    icon: <TuneOutlinedIcon />,
    label: 'Courier Setup',
    id: 'menu-dp-setup',
    displayInSidebar: true,
    component: <CourierSetup />,
  },
  /*
    The five original paths, kept alive. Each renders the same tabbed page, which reads the tab
    from the path it was reached by, so an old link or a bookmark opens on the right tab instead
    of 404ing. Not in the sidebar — the tabs are the navigation now.
  */
  ...SETUP_DIMENSIONS.map((d) => ({
    ...base,
    pathname: dpPath(d.route),
    label: d.label,
    displayInSidebar: false,
    component: <CourierSetup />,
  })),
  {
    ...base,
    // Reached from Settings → Couriers Setup and Manage, not the Direct nav: enabling couriers
    // is account-wide configuration, not a Direct setup step.
    pathname: SETTINGS_PATHS.manageCouriers,
    label: 'Couriers',
    id: 'menu-dp-availability',
    displayInSidebar: false,
    component: <CourierAvailability />,
  },

  // ── Analytics: sits with Control Tower under PERFORMANCE & ANALYTICS ──
  {
    ...base,
    group: 'GENERAL',
    zone: 'GENERAL',
    pathname: dpPath('/performance'),
    icon: <InsightsOutlinedIcon />,
    label: 'ProShip Direct Performance',
    id: 'menu-dp-performance',
    displayInSidebar: true,
    /*
      IN THE NAV ONLY ONCE A PLAN IS LIVE. Everything this page charts — Direct vs ProShip
      split, per-courier cost and TAT, mode comparison — is a comparison against shipments
      that only exist once Direct is allocating. Before activation every series is zero, so
      the entry offered a merchant a page that could only disappoint them.

      The ROUTE stays registered whatever the plan says. Hiding the nav entry is a tidiness
      decision; unregistering the path would turn a bookmark or a link in an email into a
      404, and would break the return trip after a plan lapses.
    */
    requiresDirectPlan: true,
    component: <DirectPerformance />,
  },

  // ── Reached from Settings → Couriers Setup and Manage ──
  {
    ...base,
    pathname: SETTINGS_PATHS.rules,
    label: 'Shipping Rules',
    displayInSidebar: false,
    component: <ShippingRules />,
  },
  {
    // The builder in its three states: create, ?clone=id, ?view=id. Not in the sidebar — it is
    // always entered from the rules list.
    ...base,
    pathname: SETTINGS_PATHS.ruleBuilder,
    label: 'Rule Builder',
    displayInSidebar: false,
    component: <RuleBuilder />,
  },
  {
    // The no-rules path: one allocation preference for everything. Reached from Settings, beside
    // the courier list it applies to.
    ...base,
    pathname: SETTINGS_PATHS.priority,
    label: 'Courier Priority',
    displayInSidebar: false,
    component: <CourierPriority />,
  },
  {
    ...base,
    pathname: SETTINGS_PATHS.pace,
    label: 'PACE Engine',
    displayInSidebar: false,
    component: <PaceEngine />,
  },

  // ── Reached from Settings → Brand Setup ──
  {
    // Not a Direct feature and not a fixture: the merchant's own catalogue, on the same
    // product_master collection the admin panel edits. Lives here only because this module is
    // where the Settings pages are being built.
    ...base,
    roles: SETTINGS_ROLES,
    pathname: dpPath('/product-master'),
    label: 'Product Master',
    displayInSidebar: false,
    component: <ProductMaster />,
  },
  /*
    NO TRACKING PAGE HERE ANY MORE. This module had its own, built while the real one was
    assumed not to exist; it turned out to be on origin/dev, reading and writing
    merchantDesc/config/tracking and previewing the actual buyer-facing renderer. The two ran
    side by side in Settings long enough to compare, and the real one won.

    It now lives at /tracking-config (src/app/components/tracking/TrackingConfig.js), with the
    analytics panel, the subdomain chooser and the products section brought across.
  */
  {
    ...base,
    roles: SETTINGS_ROLES,
    pathname: SETTINGS_PATHS.labelConfig,
    label: 'Label Configuration',
    displayInSidebar: false,
    component: <LabelConfig />,
  },
  {
    // Returns & QC is its own module rather than a Direct setup step: what the rider checks at
    // the door is a returns-policy decision, not a courier-contract one.
    ...base,
    roles: SETTINGS_ROLES,
    pathname: SETTINGS_PATHS.returnsQc,
    label: 'Returns & QC',
    displayInSidebar: false,
    component: <ReturnsQc />,
  },
];

// Empty when the flag is off, so the panel is untouched until this is switched on. The sidebar
// skips groups with no visible items, so the group header disappears with it.
export const directPlanRoutes = isDirectPlanEnabled() ? routes : [];

export default directPlanRoutes;
